import type { SystemUser } from "@@/shared/users/system-user";
import { systemUserZodSchema } from "@@/shared/users/system-user";
import type { UserId } from "@@/shared/users/user-id";
import { userIdZodSchema } from "@@/shared/users/user-id";
import type { Except } from "type-fest";
import { z } from "zod";
import type { IsoAndUnixTimestamp } from "../date-time/iso-and-unix";
import { isoAndUnixZodSchema, nowIsoAndUnix } from "../date-time/iso-and-unix";
import type { EntityState } from "../entity-status";
import { entityStateZodSchema } from "../entity-status";
import type { ProviderId } from "../providers/provider-id";
import type { MessageId } from "./message-id";
import { messageIdFactory, messageIdZodSchema } from "./message-id";
import type { MessageOwnerId } from "./message-owner";
import { messageOwnerIdZodSchema } from "./message-owner";

const authorIdZodSchema = z.union([
    userIdZodSchema,
    z.literal("CUSTOMER"),
    systemUserZodSchema,
]);

type Message = {
    readonly _id: MessageId;
    readonly _type: "MESSAGE";
    readonly _createdAt: IsoAndUnixTimestamp;
    readonly _status: EntityState;
    readonly type: "TEXT" | "CUSTOMER_REQUEST";
    readonly ownerId: MessageOwnerId;
    readonly author: UserId | "CUSTOMER" | SystemUser;
    readonly message: string;
    readonly contentType?: "TEXT";
    /** NOTE Old messages before june 2025 doesn't have a providerId */
    readonly providerId?: ProviderId;
    readonly replaced?: boolean;
    readonly _replacedBy?: UserId;
    readonly replacedById?: MessageId;
};

type MessageCreateParams = Except<
    Message,
    "_id" | "_createdAt" | "_type" | "_status" | "type" | "providerId"
> & {
    _id?: MessageId;
    _createdAt?: IsoAndUnixTimestamp;
    type?: "TEXT" | "CUSTOMER_REQUEST";
    providerId: ProviderId | undefined;
};

const messageFactory = (params: MessageCreateParams): Message => {
    const now = nowIsoAndUnix();
    return {
        ...params,
        _id: params._id ?? messageIdFactory(),
        _createdAt: params._createdAt ?? now,
        _status: "ACTIVE",
        _type: "MESSAGE",
        type: params.type ?? "TEXT",
    };
};

const messageZodSchema: z.ZodSchema<Message> = z.object({
    _id: messageIdZodSchema,
    _type: z.literal("MESSAGE"),
    _createdAt: isoAndUnixZodSchema,
    _status: entityStateZodSchema,
    type: z.literal("TEXT"),
    ownerId: messageOwnerIdZodSchema,
    message: z.string(),
    author: authorIdZodSchema,

    replaced: z.boolean().optional(),
    _replacedBy: userIdZodSchema.optional(),
    replacedById: messageIdZodSchema.optional(),
});

export { messageFactory, messageZodSchema };
export type { Message };
